import React, { useState, useEffect } from "react";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import { FileArchive, AlertCircle, ChevronDown, ChevronUp } from "lucide-react";
import Papa from "papaparse";

const CSVViewer = ({ url }) => {
  const [csvData, setCsvData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleRows, setVisibleRows] = useState(100);
  const [totalRows, setTotalRows] = useState(0);
  const [increaseSize, setIncreaseSize] = useState(100);

  useEffect(() => {
    const fetchCSV = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(url);
        const text = await response.text();

        Papa.parse(text, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: true,
          complete: (results) => {
            if (results.data.length > 0) {
              const cleanData = results.data.map((row) => {
                const cleanRow = {};
                Object.keys(row).forEach((key) => {
                  const cleanKey = key.trim().replace(/\s+/g, "_");
                  cleanRow[
                    cleanKey ||
                      `column_${Math.random().toString(36).substr(2, 9)}`
                  ] = row[key];
                });
                return cleanRow;
              });

              setCsvData(cleanData);
              setTotalRows(cleanData.length);
            } else {
              setError(
                "El archivo CSV está vacío o tiene un formato incorrecto"
              );
            }
            setIsLoading(false);
          },
          error: (error) => {
            console.error("Error parsing CSV:", error);
            setError("No se pudo cargar el archivo CSV");
            setIsLoading(false);
          },
        });
      } catch (err) {
        console.error("Error fetching CSV:", err);
        setError("No se pudo cargar el archivo CSV");
        setIsLoading(false);
      }
    };

    fetchCSV();
  }, [url]);

  const handleShowMore = (amount) => {
    // Si el parámetro es una cadena, mostramos esa cantidad exacta
    if (amount === "reset") {
      setVisibleRows(100);
    } else {
      // Si no, aumentamos la cantidad visible en el incremento especificado
      const newVisibleRows = visibleRows + amount;
      setVisibleRows(Math.min(newVisibleRows, totalRows));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-red-500">
        <AlertCircle size={48} />
        <p className="mt-4 text-center">{error}</p>
      </div>
    );
  }

  if (!csvData || csvData.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <AlertCircle size={48} className="text-yellow-500" />
        <p className="mt-4 text-gray-700 text-center">
          El archivo CSV está vacío o tiene un formato incorrecto
        </p>
      </div>
    );
  }

  const columns = Object.keys(csvData[0]);

  const rowsToShow = csvData.slice(0, visibleRows);

  return (
    <div className="flex flex-col border rounded-md shadow-sm">
      <div className="flex justify-between items-center bg-gray-50 p-3 border-b">
        <div className="text-sm text-gray-500">
          Mostrando {rowsToShow.length} de {totalRows} filas
        </div>
      </div>

      <div className="overflow-auto max-h-96">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              {columns.map((column, index) => (
                <th
                  key={`header-${index}`}
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rowsToShow.map((row, rowIndex) => (
              <tr
                key={`row-${rowIndex}`}
                className={rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50"}
              >
                {columns.map((column, colIndex) => (
                  <td
                    key={`cell-${rowIndex}-${colIndex}`}
                    className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 truncate max-w-xs"
                    title={row[column]?.toString()}
                  >
                    {row[column]?.toString() || ""}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalRows > 100 && (
        <div className="flex flex-col items-center py-3 border-t">
          <div className="flex flex-row items-center gap-2 mb-2">
            <span className="text-sm text-gray-500">Mostrar:</span>
            <select
              className="border rounded px-2 py-1 text-sm"
              value={increaseSize}
              onChange={(e) => setIncreaseSize(Number(e.target.value))}
            >
              <option value={100}>100 filas</option>
              <option value={500}>500 filas</option>
              <option value={1000}>1000 filas</option>
              <option value={5000}>5000 filas</option>
            </select>

            <button
              onClick={() => handleShowMore(increaseSize)}
              disabled={visibleRows >= totalRows}
              className="flex items-center gap-1 px-4 py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronDown size={16} />
              Cargar más filas
            </button>

            {visibleRows > 100 && (
              <button
                onClick={() => handleShowMore("reset")}
                className="flex items-center gap-1 px-4 py-2 bg-gray-50 text-gray-600 rounded-md hover:bg-gray-100"
              >
                <ChevronUp size={16} />
                Volver a 100 filas
              </button>
            )}
          </div>

          <div className="flex flex-row gap-2"></div>
        </div>
      )}
    </div>
  );
};

export default function FileViewer({ url, fileType }) {
  const isCSV = () => {
    return (
      fileType === "text/csv" ||
      fileType === "application/csv" ||
      fileType === "application/vnd.ms-excel" ||
      url.toLowerCase().endsWith(".csv")
    );
  };

  // Archivos ZIP
  if (
    fileType === "application/zip" ||
    fileType === "application/x-zip-compressed" ||
    fileType === "application/x-rar-compressed"
  ) {
    return (
      <div className="flex flex-col items-center justify-center h-80">
        <FileArchive size={48} className="text-yellow-500" />
        <p className="mt-4 text-gray-700 text-center">
          This is a compressed archive. <br /> File preview is not available for
          this file type
        </p>
      </div>
    );
  }

  if (isCSV()) {
    return <CSVViewer url={url} />;
  }

  // Otros tipos de archivo
  const docs = [
    {
      uri: url,
    },
  ];

  return (
    <div className="border rounded-md shadow-sm overflow-hidden">
      <DocViewer
        prefetchMethod="GET"
        pluginRenderers={DocViewerRenderers}
        documents={docs}
        config={{
          header: {
            disableHeader: true,
            disableFileName: true,
            retainURLParams: false,
          },
        }}
        style={{
          width: "100%",
          height: "70vh",
          overflow: "hidden",
        }}
        className="fileviewer"
      />
    </div>
  );
}
